// 系统：OpenClaw 版本、模型、浏览器 tabs、WhatsApp 连接；WhatsApp 扫码登录、发测试消息；提醒号码与暂停派发
import { useEffect, useState } from "react";
import { api, errorText } from "../../lib/api";
import { useAlerts } from "../../lib/alerts";
import { useAction, useLoad } from "../../lib/hooks";
import type { SystemInfo } from "../../lib/types";
import { Badge } from "../../components/Badges";
import { ColumnPicker, DataTable, useHiddenColumns, type Column } from "../../components/DataTable";
import { SubTabs, SYSTEM_TABS } from "../../components/SubTabs";
import { PageHeading } from "../../components/PageHeading";

type BrowserTab = Extract<SystemInfo["browserTabs"], unknown[]>[number];

const TAB_COLUMNS: Column<BrowserTab>[] = [
  {
    key: "title",
    label: "标题",
    fixed: true,
    render: (t) => (
      <span className="cell-clip" title={t.title}>
        {t.title || "（无标题）"}
      </span>
    ),
  },
  {
    key: "url",
    label: "地址",
    render: (t) => (
      <span className="cell-clip mono" style={{ maxWidth: 420 }} title={t.url}>
        {t.url}
      </span>
    ),
  },
  { key: "id", label: "标签页 id", render: (t) => <span className="mono muted">{t.id}</span> },
];

const PHONE_RE = /^\+\d{8,15}$/;

function parsePhones(s: string): string[] {
  return s
    .split(/[\s,，;；]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

export function SystemPage() {
  const sys = useLoad(() => api.system(), []);
  const settings = useLoad(() => api.settings(), []);
  const { toast } = useAlerts();
  const act = useAction();
  const [clerks, setClerks] = useState("");
  const [boss, setBoss] = useState("");
  const [paused, setPaused] = useState(false);
  const [qr, setQr] = useState<{ img: string | null; message: string } | null>(null);
  const [waState, setWaState] = useState<string | null>(null);
  const [testTo, setTestTo] = useState("");
  const [hiddenTabs, toggleTabCol] = useHiddenColumns("csp-hidden-columns-browser-tabs");

  useEffect(() => {
    if (!settings.data) return;
    setClerks(settings.data.whatsappClerks.join("\n"));
    setBoss(settings.data.whatsappBoss.join("\n"));
    setPaused(settings.data.paused);
  }, [settings.data]);

  const saveSettings = async () => {
    const c = parsePhones(clerks);
    const b = parsePhones(boss);
    const bad = [...c, ...b].filter((p) => !PHONE_RE.test(p));
    if (bad.length) return act.setError(`号码格式不对：${bad.join("、")}（应为 +6591234567）`);
    const r = await act.run("settings", () => api.updateSettings({ whatsappClerks: c, whatsappBoss: b, paused }));
    if (r) {
      toast("ok", "设置已保存");
      void settings.reload();
    }
  };

  const waLogin = async () => {
    setWaState("正在获取二维码…");
    setQr(null);
    try {
      const s = await api.waLoginStart();
      setQr({ img: s.qrDataUrl, message: s.message });
      setWaState("请用手机 WhatsApp 扫码（设置 → 已连接的设备 → 连接设备），等待结果…");
      const w = await api.waLoginWait(s.sessionKey);
      setWaState(w.connected ? `✅ 已连接：${w.message}` : `未连接：${w.message}`);
      if (w.connected) setQr(null);
      void sys.reload();
    } catch (e) {
      setWaState(`失败：${errorText(e)}`);
    }
  };

  const waTest = async () => {
    if (!PHONE_RE.test(testTo)) return act.setError("号码格式：+6591234567");
    const r = await act.run("test", () => api.waTest(testTo));
    if (r) toast(r.ok ? "ok" : "error", r.ok ? "测试消息已发出" : "发送失败");
  };

  const s = sys.data;
  const tabs = s && Array.isArray(s.browserTabs) ? s.browserTabs : [];
  const tabsErr = s && !Array.isArray(s.browserTabs) ? s.browserTabs.error : null;

  return (
    <div>
      <PageHeading title="系统" desc="执行环境（OpenClaw、模型、浏览器）、WhatsApp 提醒通道和派发设置">
        <button className="btn" onClick={() => void sys.reload()}>
          刷新状态
        </button>
      </PageHeading>
      <SubTabs tabs={SYSTEM_TABS} />
      {settings.data?.paused && <div className="warning-block">派发已暂停：新任务会停在排队，已在跑的不受影响。到下面「设置」里取消勾选再保存即可恢复。</div>}
      {act.error && <div className="notice err">{act.error}</div>}
      {sys.error && <div className="notice err">{sys.error}</div>}
      <div className="grid grid-2">
        <div className="card">
          <h2>执行环境</h2>
          {s && (
            <dl className="kv">
              <dt>环境</dt>
              <dd>{s.env}</dd>
              <dt>OpenClaw 版本</dt>
              <dd>{s.versions.openclawVersion ?? s.versions.error ?? "—"}</dd>
              <dt>模型</dt>
              <dd>{s.versions.modelId ?? "—"}</dd>
              <dt>门户入口</dt>
              <dd className="mono">{s.portalBase}</dd>
              <dt>浏览器忙</dt>
              <dd>{s.busy ? <Badge tone="amber">忙（有任务占用）</Badge> : <Badge tone="green">空闲</Badge>}</dd>
              <dt>WhatsApp</dt>
              <dd>
                {s.whatsapp.error
                  ? <span className="err">{s.whatsapp.error}</span>
                  : s.whatsapp.connected
                    ? <Badge tone="green">已连接</Badge>
                    : <Badge tone="red">未连接</Badge>}
                {s.whatsapp.detail && <span className="small muted"> {s.whatsapp.detail}</span>}
              </dd>
            </dl>
          )}
          {sys.loading && !s && <div className="empty">正在加载…</div>}
          <div className="list-bar" style={{ marginTop: 14 }}>
            <span className="count">
              <b className="nowrap" style={{ color: "var(--fg)" }}>浏览器标签页</b> · 共 {tabs.length} 个
            </span>
            <ColumnPicker columns={TAB_COLUMNS} hidden={hiddenTabs} onToggle={toggleTabCol} />
          </div>
          {tabsErr && <div className="notice err small">{tabsErr}</div>}
          {!tabsErr && <DataTable columns={TAB_COLUMNS} rows={tabs} rowKey={(t) => t.id} hidden={hiddenTabs} empty="浏览器没有打开的标签页" />}
        </div>
        <div className="card">
          <h2>WhatsApp 登录</h2>
          <p className="muted small" style={{ marginTop: -4 }}>提醒消息从这个 WhatsApp 号发出。断开后在这里重新扫码连接</p>
          <button className="btn btn-primary" onClick={() => void waLogin()}>开始 WhatsApp 登录</button>
          {waState && <p>{waState}</p>}
          {qr?.img && <img src={qr.img} alt="WhatsApp 登录二维码" style={{ width: 280, background: "#fff", padding: 8, borderRadius: 8 }} data-testid="wa-qr" />}
          {qr?.message && <p className="small muted">{qr.message}</p>}
          <h3 style={{ marginTop: 16 }}>发测试消息</h3>
          <div className="row">
            <input style={{ width: 220 }} value={testTo} placeholder="+6591234567" onChange={(e) => setTestTo(e.target.value)} aria-label="测试号码" />
            <button className="btn" disabled={!!act.busy} onClick={() => void waTest()}>发送测试消息</button>
          </div>
        </div>
      </div>
      <div className="card">
        <h2>设置</h2>
        {settings.data && !settings.data.whatsappEnabled && <div className="notice warn">后端未开启 WhatsApp 提醒（CSP_WHATSAPP=0），号码保存了也不会推送。</div>}
        <div className="grid grid-2">
          <label className="field">
            <span className="lbl">文员提醒号码（一行一个）</span>
            <textarea value={clerks} onChange={(e) => setClerks(e.target.value)} placeholder="+6591234567" />
          </label>
          <label className="field">
            <span className="lbl">老板提醒号码（一行一个）</span>
            <textarea value={boss} onChange={(e) => setBoss(e.target.value)} placeholder="+6598765432" />
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={paused} onChange={(e) => setPaused(e.target.checked)} />
          <b>暂停派发</b>（已在跑的不受影响，新任务停在排队）
        </label>
        <div className="row end">
          <button className="btn btn-primary" disabled={!!act.busy} onClick={() => void saveSettings()}>保存设置</button>
        </div>
      </div>
    </div>
  );
}
